import {useForm, type UseFormProps} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
// import {getSavedCartError} from '../errors';
import {type ISavedCartFormValues, SavedCartFormSchema} from '../schema';

/** Default values for the form */
const defaultValues: ISavedCartFormValues = {
	title: '',
	id: -1,
};

export interface UseSavedCartFormParams
	extends UseFormProps<ISavedCartFormValues> {
	initialValues?: Partial<ISavedCartFormValues>;
	submit: (data: ISavedCartFormValues) => Promise<void>;
}

/**
 * react-hook-form for editing a saved cart
 *
 * @example
 * ```tsx
 * const {form, onSubmit, reset} = useSavedCartForm({
 * 	initialValues: {
 * 		title: 'Saved Cart Name',
 * 	},
 * 	submit: async (data) => {
 * 		// submit the form
 * 	},
 * });
 *
 * return (
 * 	<form onSubmit={onSubmit}>...</form>
 * );
 * ```
 */
export const useSavedCartForm = ({
	initialValues = defaultValues,
	submit,
	...props
}: UseSavedCartFormParams) => {
	const form = useForm<ISavedCartFormValues>({
		mode: 'onBlur',
		criteriaMode: 'all',
		defaultValues: initialValues,
		...props,
		resolver: zodResolver(SavedCartFormSchema),
	});

	/** Reset the form to the default values */
	const reset = useConstCallback((values?: Partial<ISavedCartFormValues>) =>
		form.reset({...defaultValues, ...values}),
	);

	/** Form submission handler */
	const onSubmit = form.handleSubmit(async (data) => {
		return submit(data).catch((error) => {
			return i18n.t('i18n.errorRequestFailed');
			// const _error = getSavedCartError('update', error.code);
			// form.setError('root', {
			// 	message: typeof _error === 'function' ? _error(error.message) : _error,
			// 	type: 'error',
			// });
		});
	});

	return {
		form,
		onSubmit,
		reset,
	};
};
